/**
 * Nombre de una semana del cronograma, editable en línea.
 *
 * El informe de avance arma sus filas con este mismo nombre, así que renombrar
 * una semana aquí personaliza el informe de ese proyecto. Se usa igual en el
 * cronograma general y en el cronograma interno de cada proyecto.
 */
import React, { useState, useEffect, useRef } from 'react';
import { useNotification } from '../../context/NotificationContext';
import client from '../../services/graphqlClient';
import './NombreSemanaEditable.css';

const NombreSemanaEditable = ({ item, onRenombrada }) => {
  const { success, error } = useNotification();
  const [editando, setEditando] = useState(false);
  const [valor, setValor] = useState(item.nombre || '');
  const [guardando, setGuardando] = useState(false);
  const inputRef = useRef(null);

  // Si el cronograma se recarga, el input debe reflejar el nombre nuevo.
  useEffect(() => { if (!editando) setValor(item.nombre || ''); }, [item.nombre, editando]);
  useEffect(() => { if (editando) inputRef.current?.select(); }, [editando]);

  const guardar = async () => {
    const limpio = valor.trim();
    if (!limpio) { error('El nombre no puede quedar vacío'); return; }
    if (limpio === (item.nombre || '')) { setEditando(false); return; }

    setGuardando(true);
    try {
      const r = await client.mutate(
        `mutation($id: Int!, $n: String!) { renombrarSemanaCronograma(id: $id, nombre: $n) { success message } }`,
        { id: item.id, n: limpio }
      );
      if (r.renombrarSemanaCronograma.success) {
        success('Nombre actualizado');
        setEditando(false);
        onRenombrada && onRenombrada(item.id, limpio);
      } else {
        error(r.renombrarSemanaCronograma.message || 'No se pudo renombrar');
      }
    } catch {
      error('Error al renombrar la semana');
    } finally {
      setGuardando(false);
    }
  };

  const cancelar = () => { setValor(item.nombre || ''); setEditando(false); };

  if (editando) {
    return (
      <span className="nse-edit">
        <input
          ref={inputRef}
          className="nse-input"
          value={valor}
          maxLength={255}
          disabled={guardando}
          onChange={e => setValor(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') { e.preventDefault(); guardar(); }
            if (e.key === 'Escape') { e.preventDefault(); cancelar(); }
          }}
          onClick={e => e.stopPropagation()}
        />
        <button type="button" className="nse-btn nse-ok" disabled={guardando}
          onClick={e => { e.stopPropagation(); guardar(); }} title="Guardar (Enter)">✓</button>
        <button type="button" className="nse-btn nse-no" disabled={guardando}
          onClick={e => { e.stopPropagation(); cancelar(); }} title="Cancelar (Esc)">×</button>
      </span>
    );
  }

  return (
    <span className="nse-ver">
      <strong>{item.etiqueta_semana || `Semana ${item.orden}`}: {item.nombre}</strong>
      <button
        type="button"
        className="nse-lapiz"
        title="Renombrar esta semana (también cambia el informe de avance)"
        onClick={e => { e.stopPropagation(); setEditando(true); }}
      >✎</button>
    </span>
  );
};

export default NombreSemanaEditable;
